"use client";

import type {
  ChatRequest,
  ChatResponse,
  SSEEvent,
  JudgeRequest,
  JudgeResponse,
} from "./types";
import { getAuthHeaders } from "./api";

const API_BASE = process.env.NEXT_PUBLIC_API_BASE?.trim() || "";

// =============================================================================
// SSE Streaming
// =============================================================================

/**
 * Sends a chat request via SSE and calls onEvent for each parsed event.
 * Handles ReadableStream parsing of server-sent events.
 *
 * Stream-idle watchdog: if no event of any kind (delta / done / keepalive)
 * arrives for `IDLE_TIMEOUT_MS`, the function aborts the connection via the
 * supplied signal and throws. This guards against the case where a TCP
 * keepalive elsewhere in the path (Caddy / OS) silently drops a long-tail
 * provider's connection — the reader.read() would otherwise block forever
 * and the auto-judge save-ACK chain would never fire.
 *
 * iOS canonical: ChatViewModel.swift uses URLSession's per-request
 * timeoutIntervalForRequest (default 60s on iOS) as the equivalent.
 */
// Generous: thinking/pro/deep_research modes can have multi-minute silent
// reasoning phases. 180s tolerates the longest realistic silence while still
// catching true TCP drops (the stuck-anthropic-spinner bug observed 2026-05-04
// where the 336s anthropic run completed server-side but the done event never
// reached the client). Server-side keepalive emissions would let us tighten
// this further; tracked as v1.2 follow-up.
const IDLE_TIMEOUT_MS = 180_000;

export async function streamChat(
  chatRequest: ChatRequest,
  onEvent: (event: SSEEvent) => void,
  signal?: AbortSignal
): Promise<void> {
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
    "X-Client-Platform": "web",
    ...getAuthHeaders(),
  };

  // Compose a watchdog AbortController on top of the caller's signal so the
  // watchdog can abort independently. Caller's signal forwards via listener.
  // The watchdog's abort uses message "stream-idle" so the consumer can
  // distinguish a watchdog timeout from a user-stop and fall through to the
  // record-save path instead of bailing silently.
  const watchdog = new AbortController();
  const onCallerAbort = () => {
    watchdog.abort(new DOMException("user-stop", "AbortError"));
  };
  if (signal) {
    if (signal.aborted) {
      onCallerAbort();
    } else {
      signal.addEventListener("abort", onCallerAbort);
    }
  }
  let idleTimer: ReturnType<typeof setTimeout> | null = null;
  const resetIdleTimer = () => {
    if (idleTimer) clearTimeout(idleTimer);
    idleTimer = setTimeout(() => {
      watchdog.abort(new DOMException("stream-idle", "AbortError"));
    }, IDLE_TIMEOUT_MS);
  };

  const response = await fetch(`${API_BASE}/chat/stream`, {
    method: "POST",
    headers,
    body: JSON.stringify(chatRequest),
    signal: watchdog.signal,
  });

  if (!response.ok) {
    const text = await response.text();
    throw new Error(text || `Stream request failed (${response.status})`);
  }

  if (!response.body) {
    throw new Error("Response body is null — streaming not supported");
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";
  resetIdleTimer();

  try {
    while (true) {
      const { value, done } = await reader.read();
      // Any read activity counts as progress (even a partial chunk).
      resetIdleTimer();
      if (done) break;

      buffer += decoder.decode(value, { stream: true });

      // SSE events are separated by double newlines
      const parts = buffer.split("\n\n");
      buffer = parts.pop() || "";

      for (const part of parts) {
        const lines = part.split("\n");
        const dataLines = lines
          .filter((line) => line.startsWith("data:"))
          .map((line) => line.slice(5).trim());

        if (dataLines.length === 0) continue;

        const data = dataLines.join("\n");
        try {
          const event = JSON.parse(data) as SSEEvent;
          onEvent(event);
        } catch {
          // Skip malformed JSON
          continue;
        }
      }
    }

    // Process any remaining buffer
    if (buffer.trim()) {
      const lines = buffer.split("\n");
      const dataLines = lines
        .filter((line) => line.startsWith("data:"))
        .map((line) => line.slice(5).trim());

      if (dataLines.length > 0) {
        const data = dataLines.join("\n");
        try {
          const event = JSON.parse(data) as SSEEvent;
          onEvent(event);
        } catch {
          // Skip malformed JSON
        }
      }
    }
  } finally {
    if (idleTimer) clearTimeout(idleTimer);
    if (signal) signal.removeEventListener("abort", onCallerAbort);
    reader.releaseLock();
  }
}

// =============================================================================
// Judge SSE — mirrors iOS ChatViewModel.judgeRecord (S1 of v1.1 web sprint)
// =============================================================================

// Server emits these event shapes during a judge run. See iOS canonical at
// FaceOff/Shared/State/ChatViewModel.swift:1126-1378.
export type JudgeStreamEvent =
  | { type: "starting" }
  | { type: "scoring"; verdict: JudgeResponse }
  | { type: "synthesis_delta"; delta: string; verdict: JudgeResponse }
  | { type: "synthesis_replace"; text: string; verdict: JudgeResponse }
  | { type: "verdict"; verdict: JudgeResponse }
  | { type: "error"; stage: string; detail: string; fatal: boolean };

/**
 * Number of additional attempts (beyond the first) for the initial POST /judge
 * when it fails before any SSE data has been received. HTTP 502/503/504 from
 * /judge is a known recurring transient failure; a couple of fast retries
 * clears it without locking the toolbar Judge button behind a futile 5-minute
 * recovery poll. Backoff: ~250 ms, then ~750 ms.
 */
const JUDGE_RETRY_BACKOFF_MS = [250, 750];

/**
 * Streams /judge as SSE and emits parsed events to the caller. Maintains a
 * running partial verdict internally so synthesis_delta / synthesis_replace
 * always carry forward the scoring fields (winner, scores, rationale,
 * synthesis tokens, etc.) — caller never sees a half-shaped verdict.
 *
 * Initial-POST resilience: if the `POST /judge` itself fails — an HTTP 5xx
 * response (502/503/504) or a transient network error — *before any SSE byte
 * has arrived, the POST is retried up to JUDGE_RETRY_BACKOFF_MS.length times
 * with exponential backoff. A 4xx is never retried (validation/auth/not-found
 * is permanent). Once the stream has started, no retry happens — a mid-stream
 * drop is handled by the caller's recovery poll instead.
 *
 * On non-2xx HTTP (after retries are exhausted), throws with `httpStatus` set
 * on the error so the caller can branch on 4xx (no recovery) vs 5xx.
 *
 * `onStreamStarted`, if supplied, is invoked exactly once the instant the
 * first SSE event is dispatched. The caller uses this to distinguish an
 * initial-POST failure (no recovery — there is no server-side judge) from a
 * mid-stream drop (recovery poll is meaningful — the server may still finish).
 */
export async function streamJudge(
  request: JudgeRequest,
  onEvent: (event: JudgeStreamEvent) => void,
  signal?: AbortSignal,
  onStreamStarted?: () => void
): Promise<void> {
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
    Accept: "text/event-stream",
    "X-Client-Platform": "web",
    ...getAuthHeaders(),
  };

  // Issue the initial POST with bounded retries. Only failures *before any
  // SSE data* are retried here — a 5xx response or a network error throwing
  // out of fetch(). A 4xx is permanent and bails immediately.
  let response: Response | null = null;
  let lastErr: (Error & { httpStatus?: number }) | null = null;
  const maxAttempts = JUDGE_RETRY_BACKOFF_MS.length + 1;
  for (let attempt = 0; attempt < maxAttempts; attempt++) {
    if (signal?.aborted) {
      throw new DOMException("aborted", "AbortError");
    }
    try {
      const resp = await fetch(`${API_BASE}/judge`, {
        method: "POST",
        headers,
        body: JSON.stringify(request),
        signal,
      });
      if (resp.ok) {
        response = resp;
        break;
      }
      const text = await resp.text().catch(() => "");
      const err = new Error(
        text || `Judge request failed (${resp.status})`
      ) as Error & { httpStatus?: number };
      err.httpStatus = resp.status;
      lastErr = err;
      // 4xx — permanent, do not retry.
      if (resp.status >= 400 && resp.status < 500) {
        throw err;
      }
      // 5xx — retry if attempts remain.
    } catch (e) {
      const err = e as Error & { httpStatus?: number };
      // A thrown 4xx (above) or an abort is permanent — propagate immediately.
      if (
        (err.httpStatus && err.httpStatus >= 400 && err.httpStatus < 500) ||
        err.name === "AbortError"
      ) {
        throw err;
      }
      // 5xx (re-thrown) or a network error — retry if attempts remain.
      lastErr = err;
    }
    if (attempt < maxAttempts - 1) {
      await new Promise((r) => setTimeout(r, JUDGE_RETRY_BACKOFF_MS[attempt]));
    }
  }

  if (!response) {
    throw lastErr ?? new Error("Judge request failed");
  }

  if (!response.body) {
    throw new Error("Response body is null — streaming not supported");
  }

  let streamStartedNotified = false;
  const notifyStreamStarted = () => {
    if (streamStartedNotified) return;
    streamStartedNotified = true;
    onStreamStarted?.();
  };

  // Partial state — survives across synthesis_delta / synthesis_replace so
  // the verdict object the caller sees is always coherent (no mid-stream
  // dropout of scoring fields). iOS canonical: ChatViewModel.swift:1126-1142.
  let partialWinner: string | undefined;
  let partialRationale: string | undefined;
  let partialScores: Record<string, number> | undefined;
  let partialScoringProvider: string | undefined;
  let partialSynthesis = "";
  let partialScoringModel: string | undefined;
  let partialSynthesisModel: string | undefined;
  let partialScoringDurationMs: number | undefined;
  let partialSynthesisMaxTokens: number | undefined;
  let partialSynthesisBudgetTokens: number | undefined;
  // Panel scoring (2026-05-05+): server emits the full panel composition
  // and per-scorer breakdown on both `scoring` and `verdict` events. Latch
  // these on the scoring event so the JudgeCard's "Judges: A, B, C" meta
  // line stays correct across synthesis_delta events that re-emit verdict.
  let partialPanelScoringProviders: string[] | undefined;
  let partialPanelScoringModels: string[] | undefined;
  let partialPanelScores: Record<string, Record<string, number>> | undefined;
  const isAuto = request.is_auto === true;

  const buildVerdict = (overrides: Partial<JudgeResponse> = {}): JudgeResponse => ({
    winner: partialWinner ?? "",
    rationale: partialRationale ?? "",
    synthesis: partialSynthesis,
    scores: partialScores,
    scoring_provider: partialScoringProvider ?? null,
    auto: isAuto,
    scoring_model: partialScoringModel,
    synthesis_model: partialSynthesisModel,
    scoring_duration_ms: partialScoringDurationMs,
    synthesis_max_tokens: partialSynthesisMaxTokens,
    synthesis_budget_tokens: partialSynthesisBudgetTokens,
    panel_scoring_providers: partialPanelScoringProviders,
    panel_scoring_models: partialPanelScoringModels,
    panel_scores: partialPanelScores,
    ...overrides,
  });

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";

  const dispatchEvent = (raw: Record<string, unknown>) => {
    // First successfully parsed SSE event ⇒ the stream is genuinely open.
    // Tells the caller this is no longer an initial-POST failure path.
    notifyStreamStarted();
    const type = (raw.type as string) || "";
    switch (type) {
      case "starting":
        onEvent({ type: "starting" });
        break;
      case "scoring": {
        partialWinner = raw.winner as string | undefined;
        partialRationale = raw.rationale as string | undefined;
        partialScores = (raw.scores as Record<string, number> | undefined) ?? partialScores;
        partialScoringProvider =
          (raw.scoring_provider as string | undefined) ?? partialScoringProvider;
        partialScoringModel =
          (raw.scoring_model as string | undefined) ?? partialScoringModel;
        partialSynthesisModel =
          (raw.synthesis_model as string | undefined) ?? partialSynthesisModel;
        if (typeof raw.scoring_duration_ms === "number") {
          partialScoringDurationMs = raw.scoring_duration_ms as number;
        }
        if (typeof raw.synthesis_max_tokens === "number") {
          partialSynthesisMaxTokens = raw.synthesis_max_tokens as number;
        }
        if (typeof raw.synthesis_budget_tokens === "number") {
          partialSynthesisBudgetTokens = raw.synthesis_budget_tokens as number;
        }
        if (Array.isArray(raw.panel_scoring_providers)) {
          partialPanelScoringProviders = raw.panel_scoring_providers as string[];
        }
        if (Array.isArray(raw.panel_scoring_models)) {
          partialPanelScoringModels = raw.panel_scoring_models as string[];
        }
        if (raw.panel_scores && typeof raw.panel_scores === "object") {
          partialPanelScores = raw.panel_scores as Record<string, Record<string, number>>;
        }
        onEvent({ type: "scoring", verdict: buildVerdict() });
        break;
      }
      case "synthesis_delta": {
        const delta = (raw.delta as string) || "";
        partialSynthesis += delta;
        onEvent({ type: "synthesis_delta", delta, verdict: buildVerdict() });
        break;
      }
      case "synthesis_replace": {
        const text = (raw.text as string) || "";
        partialSynthesis = text;
        onEvent({ type: "synthesis_replace", text, verdict: buildVerdict() });
        break;
      }
      case "verdict": {
        // Final shape — server may include fields the partials missed; prefer
        // server values, fall back to partials we already captured.
        const final = buildVerdict({
          winner: (raw.winner as string) ?? partialWinner ?? "",
          rationale: (raw.rationale as string) ?? partialRationale ?? "",
          synthesis: (raw.synthesis as string) ?? partialSynthesis,
          scores: (raw.scores as Record<string, number> | undefined) ?? partialScores,
          scoring_provider:
            (raw.scoring_provider as string | undefined) ??
            partialScoringProvider ??
            null,
          auto: (raw.auto as boolean | undefined) ?? isAuto,
          scoring_model: (raw.scoring_model as string | undefined) ?? partialScoringModel,
          synthesis_model:
            (raw.synthesis_model as string | undefined) ?? partialSynthesisModel,
          scoring_duration_ms:
            typeof raw.scoring_duration_ms === "number"
              ? (raw.scoring_duration_ms as number)
              : partialScoringDurationMs,
          synthesis_duration_ms:
            typeof raw.synthesis_duration_ms === "number"
              ? (raw.synthesis_duration_ms as number)
              : undefined,
          synthesis_max_tokens:
            typeof raw.synthesis_max_tokens === "number"
              ? (raw.synthesis_max_tokens as number)
              : partialSynthesisMaxTokens,
          synthesis_budget_tokens:
            typeof raw.synthesis_budget_tokens === "number"
              ? (raw.synthesis_budget_tokens as number)
              : partialSynthesisBudgetTokens,
          panel_scoring_providers:
            (raw.panel_scoring_providers as string[] | undefined) ??
            partialPanelScoringProviders,
          panel_scoring_models:
            (raw.panel_scoring_models as string[] | undefined) ??
            partialPanelScoringModels,
          panel_scores:
            (raw.panel_scores as Record<string, Record<string, number>> | undefined) ??
            partialPanelScores,
        });
        onEvent({ type: "verdict", verdict: final });
        break;
      }
      case "error": {
        onEvent({
          type: "error",
          stage: (raw.stage as string) ?? "unknown",
          detail: (raw.detail as string) ?? "judge error",
          fatal: (raw.fatal as boolean) === true,
        });
        break;
      }
      default:
        // Unknown event type — ignore (forward-compat).
        break;
    }
  };

  try {
    while (true) {
      const { value, done } = await reader.read();
      if (done) break;

      buffer += decoder.decode(value, { stream: true });

      const parts = buffer.split("\n\n");
      buffer = parts.pop() || "";

      for (const part of parts) {
        const lines = part.split("\n");
        const dataLines = lines
          .filter((line) => line.startsWith("data:"))
          .map((line) => line.slice(5).trim());
        if (dataLines.length === 0) continue;
        const data = dataLines.join("\n");
        try {
          const raw = JSON.parse(data) as Record<string, unknown>;
          dispatchEvent(raw);
        } catch {
          // skip malformed JSON
        }
      }
    }

    if (buffer.trim()) {
      const lines = buffer.split("\n");
      const dataLines = lines
        .filter((line) => line.startsWith("data:"))
        .map((line) => line.slice(5).trim());
      if (dataLines.length > 0) {
        const data = dataLines.join("\n");
        try {
          const raw = JSON.parse(data) as Record<string, unknown>;
          dispatchEvent(raw);
        } catch {
          // skip
        }
      }
    }
  } finally {
    reader.releaseLock();
  }
}

// =============================================================================
// Stream Resumption
// =============================================================================

/**
 * Attempts to resume a stream by fetching buffered results from the server.
 * Returns null if the stream is not found or not yet complete.
 */
export async function resumeStream(
  streamId: string
): Promise<ChatResponse | null> {
  try {
    const response = await fetch(
      `${API_BASE}/chat/stream/${streamId}/result`,
      { headers: getAuthHeaders() }
    );

    if (!response.ok) {
      return null;
    }

    return (await response.json()) as ChatResponse;
  } catch {
    return null;
  }
}
